import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { nanoid } from 'nanoid';
import { getDialogPosition } from 'libs/dialog-position';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { txt } from 'libs/i18n';
import clsx from 'clsx';
import DraggableModal from 'components/DraggableModal';
import { filterKeywordsOnly, getContrastColor } from '../keywords';
import styles from './style.module.less';
const KeywordPreview = ({ keywordsHighlight, keywords, onClose, }) => {
    const [resultsPositions, setResultsPositions] = useState({});
    const [activeKeyword, setActiveKeyword] = useState(null);
    const [uniquesCategories, setUniqueCategories] = useState([]);
    const highlightedKeywords = filterKeywordsOnly(keywordsHighlight);
    useEffect(() => {
        const createUniqueKeywordCategories = () => {
            const result = {};
            highlightedKeywords.forEach((keyword) => {
                var _a;
                if (keyword.category !== undefined) {
                    // NOTE: If a unique category is not defined yet, create it.
                    if (result[keyword.category] === undefined) {
                        result[keyword.category] = {
                            currentIndex: 0,
                            keywordInfo: [],
                        };
                    }
                    (_a = result[keyword.category]) === null || _a === void 0 ? void 0 : _a.keywordInfo.push(keyword);
                }
            });
            const firstKeyword = highlightedKeywords.length > 0 ? highlightedKeywords[0] : null;
            if (firstKeyword !== null && activeKeyword === null && highlightedKeywords.length > 0) {
                setActiveKeyword(highlightedKeywords[0]);
            }
            setResultsPositions(result);
            setUniqueCategories(Object.keys(result));
        };
        createUniqueKeywordCategories();
    }, [keywordsHighlight]);
    useEffect(() => {
        if (activeKeyword === null
            || activeKeyword.highlight === undefined
            || activeKeyword.timestampSec === undefined) {
            return;
        }
        void keywords.highlightActiveKeyword({
            timestamp: activeKeyword.timestampSec,
            text: activeKeyword.text,
            highlight: activeKeyword.highlight,
        });
    }, [activeKeyword]);
    const getCorrectedCurrentIndex = (direction, result) => {
        if (direction === 'previous' && result.currentIndex <= 0) {
            return result.keywordInfo.length - 1;
        }
        if (direction === 'next' && result.currentIndex >= result.keywordInfo.length - 1) {
            return 0;
        }
        return direction === 'previous' ? result.currentIndex - 1 : result.currentIndex + 1;
    };
    const handleChangePosition = (keyword, direction) => {
        // NOTE: Decolor the previous active keyword. Usecase: the user is switching between
        // active keyword by changing the result position.
        if (activeKeyword !== null && keyword.category !== activeKeyword.category) {
            if (activeKeyword.timestampSec !== undefined) {
                void keywords.toneDownKeyword(activeKeyword.timestampSec, activeKeyword);
            }
        }
        highlightedKeywords.forEach((item) => {
            if (item.timestampSec === keyword.timestampSec && keyword.category !== undefined) {
                const result = resultsPositions[keyword.category];
                if (result === undefined) {
                    return;
                }
                const newCurrentIndex = getCorrectedCurrentIndex(direction, result);
                setResultsPositions(Object.assign(Object.assign({}, resultsPositions), { [keyword.category]: Object.assign(Object.assign({}, result), { currentIndex: newCurrentIndex }) }));
                setActiveKeyword(result.keywordInfo[newCurrentIndex]);
            }
        });
    };
    const handleKeywordClick = (keyword) => {
        if (activeKeyword !== null && activeKeyword.category !== undefined) {
            const previousPosition = resultsPositions[activeKeyword.category];
            if (previousPosition === undefined) {
                return;
            }
        }
        setActiveKeyword(keyword);
    };
    const closeKeywordModal = () => {
        onClose();
        if (activeKeyword !== null && activeKeyword.timestampSec !== undefined) {
            void keywords.toneDownKeyword(activeKeyword.timestampSec, activeKeyword);
        }
    };
    const getCategories = uniquesCategories.map((category) => {
        const result = resultsPositions[category];
        if (result === undefined) {
            return [];
        }
        const keyword = result.keywordInfo[result.currentIndex];
        const getKeywordBgColor = () => {
            if (keyword.highlight === undefined) {
                return '';
            }
            if (keyword.highlight === 'default') {
                return 'var(--keyword-bgcolor)';
            }
            return keyword.highlight;
        };
        return (_jsxs("div", { className: styles.keyword, children: [_jsx("button", { type: "button", style: {
                        backgroundColor: getKeywordBgColor(),
                        color: getContrastColor(getKeywordBgColor()),
                    }, className: clsx(styles.keywordText, {
                        [styles.keywordTextHighlighted]: keyword.category === (activeKeyword === null || activeKeyword === void 0 ? void 0 : activeKeyword.category),
                    }), onClick: () => handleKeywordClick(keyword), children: category === '' ? txt('noCategory') : category }), _jsxs("div", { className: styles.keywordPosition, children: [_jsx("span", { className: keyword.category !== (activeKeyword === null || activeKeyword === void 0 ? void 0 : activeKeyword.category)
                                ? styles.inactiveKeyword : styles.activeKeyword, children: `${result.currentIndex + 1} / ${result.keywordInfo.length}` }), _jsx("button", { type: "button", onClick: () => handleChangePosition(keyword, 'previous'), className: keyword.category !== (activeKeyword === null || activeKeyword === void 0 ? void 0 : activeKeyword.category)
                                ? styles.inactiveKeyword : styles.activeKeyword, children: _jsx(ArrowUpOutlined, {}) }), _jsx("button", { type: "button", onClick: () => handleChangePosition(keyword, 'next'), className: keyword.category !== (activeKeyword === null || activeKeyword === void 0 ? void 0 : activeKeyword.category)
                                ? styles.inactiveKeyword : styles.activeKeyword, children: _jsx(ArrowDownOutlined, {}) })] })] }, nanoid(5)));
    });
    return (_jsx(DraggableModal, { bounds: "body", title: txt('keywordPreview'), onClose: closeKeywordModal, defaultPosition: getDialogPosition({ width: 500, height: 300 }), children: _jsx("div", { className: styles.dragModal, children: _jsx("div", { className: styles.content, children: highlightedKeywords.length === 0 ? txt('noKeywords') : getCategories }) }) }));
};
export default KeywordPreview;
